import { ColumnDef, createColumnHelper, Row, RowData } from "@tanstack/react-table";
import { Property } from "../rest/responses/Property";
import { Accessor, PropertyAttribute } from "../types/types";
import { useMemo, type ReactNode } from "react";
import { Checkbox } from "semantic-ui-react";
import { Link } from "../shims/useNavigate";
import { Link2 } from "react-feather";
import usePropertyAccessors from "./usePropertyAccessors";

declare module "@tanstack/react-table" {
	interface ColumnMeta<TData extends RowData, TValue> {
		enableBulkEdit: boolean;
		isFixed?: boolean;
	}
}

const columnHelper = createColumnHelper<Property>();

// Attributes that cannot be sorted/filtered.
export const NON_QUERYABLE_ACCESSORS: Accessor[] = ["image_meta.image_urls"];
export const NON_QUERYABLE_ATTRIBUTES: PropertyAttribute[] = ["group_names"];

/** Format values so that JSON objects can be rendered in the table as string. */
function formatTableCellValue(value: unknown): ReactNode {
	if (value === null || value === undefined) return value;
	if (typeof value === "object") return JSON.stringify(value, undefined, 1);
	return value as ReactNode;
}

export default function useTableColumns(accessors: Accessor[], propertyAttributes: PropertyAttribute[]) {
	const { getFriendlyColumnName, getColumnWidth } = usePropertyAccessors();
	// Remove leading slash
	const linkFrom = window.location.pathname.slice(1);

	return useMemo(
		() => [
			{
				id: "selection",
				header: ({ table }) => (
					<Checkbox
						checked={table.getIsAllRowsSelected()}
						indeterminate={table.getIsSomeRowsSelected()}
						onChange={table.getToggleAllRowsSelectedHandler()} //or getToggleAllPageRowsSelectedHandler
					/>
				),
				cell: ({ row }: { row: Row<Property> }) => (
					<Checkbox
						checked={row.getIsSelected()}
						disabled={!row.getCanSelect()}
						onChange={row.getToggleSelectedHandler()}
					/>
				),
				enableSorting: false,
				enableResizing: false,
				maxSize: 75,
				meta: {
					enableBulkEdit: false,
					isFixed: true,
				},
			},
			{
				id: "link",
				header: "Link",
				cell: ({ row }: { row: Row<Property> }) => (
					<Link
						to={{
							pathname: `/epc-data/${row.id}`,
							search: `?from=${linkFrom}`,
						}}
						target="_blank"
						rel="noopener noreferrer"
					>
						<Link2 />
					</Link>
				),
				enableSorting: false,
				enableResizing: false,
				maxSize: 75,
				meta: {
					enableBulkEdit: false,
					isFixed: true,
				},
			},

			...propertyAttributes.map((attribute) =>
				columnHelper.accessor(attribute, {
					id: attribute,
					header: () => getFriendlyColumnName(attribute),
					cell: (info) => formatTableCellValue(info.getValue()),
					size: getColumnWidth(attribute),
					enableSorting: !NON_QUERYABLE_ATTRIBUTES.includes(attribute),
					meta: {
						enableBulkEdit: false,
					},
				})
			),

			...(accessors.map((accessor) => {
				const columnInfo: ColumnDef<Property, any> = {
					id: accessor,
					header: () => getFriendlyColumnName(accessor),
					cell: (info) => formatTableCellValue(info.getValue()),
					size: getColumnWidth(accessor),
					enableSorting: !NON_QUERYABLE_ACCESSORS.includes(accessor),
				};

				if (accessor === "image_meta.image_urls") {
					columnInfo["minSize"] = 240;
				}

				return columnHelper.accessor((row) => row[accessor], columnInfo);
			}) as ColumnDef<Property, any>[]),
		],
		[accessors, propertyAttributes, getFriendlyColumnName, linkFrom, getColumnWidth]
	);
}
